import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, ChevronRight, Clock, FileText, Lock, ShieldCheck, Sparkles, UploadCloud } from "lucide-react";

export function UltimateGuide() {
  return (
    <article className="panel legal" style={{ maxWidth: 840, margin: "0 auto", padding: "36px 32px" }}>
      <header style={{ marginBottom: 32 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 14 }}>
          <BookOpen size={12} /> Comprehensive Technical Reference · 2026 Edition
        </div>
        <h1 style={{ fontSize: "clamp(28px, 4.5vw, 42px)", lineHeight: 1.12, margin: "0 0 16px", fontWeight: 900, letterSpacing: "-0.03em" }}>
          The Ultimate Guide to Large File Transfer in 2026: Protocols, Security, Compression &amp; Zero-Egress Economics
        </h1>
        <div style={{ display: "flex", gap: 18, color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Clock size={14} /> 22 min read</span>
          <span>·</span>
          <span>4,200 words</span>
          <span>·</span>
          <span>Updated September 2026</span>
        </div>
        <p className="hint" style={{ fontSize: 16, lineHeight: 1.65, margin: 0, color: "#d0dfd0" }}>
          A definitive, ground-up architectural analysis of moving gigabyte-scale datasets across the modern internet.
          Covering network protocol physics, the cloud egress tax, cryptographic threat models, platform limits,
          and client-side zero-knowledge streaming.
        </p>
      </header>

      {/* Table of Contents HUD */}
      <nav style={{ padding: 20, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 36 }}>
        <b style={{ color: "var(--lime)", fontSize: 14, letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: 12 }}>
          Table of Contents
        </b>
        <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <li><a href="#part-1">Part 1: The Physics and Architecture of Moving Gigabytes</a></li>
          <li><a href="#part-2">Part 2: The Egress Trap — Why Cloud Giants Charge $90/TB</a></li>
          <li><a href="#part-3">Part 3: Cryptography Breakdown — TLS vs At-Rest vs True Zero-Knowledge</a></li>
          <li><a href="#part-4">Part 4: The 2026 Platform Attachment Limit Encyclopedia</a></li>
          <li><a href="#part-5">Part 5: Device-to-Device Transfer Matrix (iOS, Android, Windows, Mac)</a></li>
          <li><a href="#part-6">Part 6: Troubleshooting Bounces, Corrupted Streams &amp; Memory Limits</a></li>
          <li><a href="#part-7">Part 7: Legal, Regulatory &amp; Compliance Frameworks (GDPR &amp; HIPAA)</a></li>
        </ol>
      </nav>

      {/* Action Banner */}
      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 36, textAlign: "center" }}>
        <b style={{ fontSize: 16, color: "var(--text)" }}>Need to send a file right now?</b>
        <p className="hint" style={{ margin: "4px 0 16px" }}>
          Up to 5 GB free · Browser AES-256-GCM encryption · Zero accounts, instant streaming
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <span>Open Transfer Dropzone</span> <ArrowRight size={15} />
        </Link>
      </div>

      <section id="part-1" style={{ marginBottom: 36 }}>
        <h2>Part 1: The Physics and Architecture of Moving Gigabytes</h2>
        <p>
          Moving a 10 kilobyte text document across the internet is trivial: packet loss is masked by TCP retransmissions, latency
          is negligible, and server memory allocations are microscopic. But when a creator or enterprise uploads a 3 gigabyte video
          master or a 10 gigabyte virtual machine image, the underlying physics of computer networking and browser runtime architecture
          present three fatal bottlenecks.
        </p>

        <h3>1.1 TCP Window Scaling and Bandwidth-Delay Product (BDP)</h3>
        <p>
          The throughput of any single TCP connection is mathematically bounded by the Bandwidth-Delay Product:
          <code>Throughput ≤ TCP_Window_Size / Round_Trip_Time (RTT)</code>. On high-latency international links (e.g. London to
          Sydney with a 280 ms RTT), an un-tuned TCP connection caps at a fraction of broadband capacity regardless of whether you pay
          for gigabit fiber.
        </p>
        <p>
          To saturate modern connections, modern file transfer systems employ <b>multi-part parallelization</b>: slicing the binary
          stream into uniform 8 MB to 64 MB chunks and dispatching 3 to 4 concurrent HTTP/2 or HTTP/3 streams. LimeShare's engine
          automatically tunes part sizing based on total container length, keeping concurrency bounded so mobile network buffers do
          not suffer from bufferbloat.
        </p>

        <h3>1.2 The Browser RAM Ceiling (O(1) Memory Streaming)</h3>
        <p>
          Historically, JavaScript file-sharing utilities attempted to read entire files into memory using <code>FileReader.readAsArrayBuffer()</code>
          or <code>Response.blob()</code>. On desktop computers with 32 GB of RAM, this causes sluggishness. On mobile devices
          (such as an iPhone with Safari's strict 1.4 GB WebProcess memory limit), attempting to buffer a 2 GB file triggers an
          instantaneous WebKit crash (`Out of Memory / WebProcess terminated`).
        </p>
        <p>
          LimeShare was engineered strictly around the <b>Web Streams API</b>. Chunks are ingested in 4 MiB portions, encrypted in
          Web Workers, transferred to storage, and immediately garbage-collected. The working set memory remains strictly under 50 MB
          whether you send a 5 MB photo or a 50 GB master archive.
        </p>
      </section>

      <section id="part-2" style={{ marginBottom: 36 }}>
        <h2>Part 2: The Egress Trap — Why Cloud Giants Charge $90/TB</h2>
        <p>
          Traditional file-sharing services (WeTransfer, Dropbox, Box, Hightail) were architected on legacy cloud providers like
          Amazon Web Services (AWS S3 + CloudFront) or Google Cloud Storage. On AWS, storing 1 terabyte of data costs roughly $23/month.
          However, the hidden trap is <b>data egress</b> (bandwidth out to the internet).
        </p>
        <p>
          AWS charges approximately $0.09 per gigabyte of outbound internet traffic. If 10,000 recipients download a 1 GB file, the
          resulting 10 TB of egress generates an infrastructure invoice of <b>$900 for a single transfer</b>.
        </p>
        <p>
          To survive, legacy services were forced to degrade their product: slashing free limits to 2 GB, plastering full-screen
          advertisements on download pages, throttling download speeds for non-paying users, and aggressively expiring links after 3 days.
        </p>
        <p>
          LimeShare is built on a modern infrastructure paradigm: <b>distributed zero-egress encrypted object storage</b>. The platform
          operates its own global optical transit network across 330+ cities worldwide, eliminating bandwidth markups entirely.
          This cost asymmetry is what allows LimeShare to deliver unthrottled gigabit speeds, zero advertisements, and generous 3 GB
          free transfers sustainably.
        </p>
      </section>

      <section id="part-3" style={{ marginBottom: 36 }}>
        <h2>Part 3: Cryptography Breakdown — TLS vs At-Rest vs True Zero-Knowledge</h2>
        <p>
          Marketing materials routinely claim that a cloud service is "secure" or "encrypted with 256-bit AES." In practice, 99% of
          commercial file transfer tools utilize server-side encryption where the company holds the keys.
        </p>

        <table>
          <thead>
            <tr>
              <th>Layer</th>
              <th>What is protected</th>
              <th>Who holds the keys</th>
              <th>Vulnerable to provider breach?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>TLS / HTTPS</b></td>
              <td>Network packets in transit</td>
              <td>Web server</td>
              <td>Yes (server decrypts payload)</td>
            </tr>
            <tr>
              <td><b>Server-Side Encryption (SSE)</b></td>
              <td>Physical hard drives in data center</td>
              <td>Cloud provider (AWS / Google)</td>
              <td>Yes (provider can inspect files)</td>
            </tr>
            <tr>
              <td><b>Zero-Knowledge (LSC1 STREAM)</b></td>
              <td>Binary payload, metadata &amp; manifest</td>
              <td><b>Sender &amp; Recipient only</b></td>
              <td><b>No (mathematically unreadable)</b></td>
            </tr>
          </tbody>
        </table>

        <h3>The Mechanics of LSC1 Streaming AEAD</h3>
        <p>
          LimeShare employs the open <b>LSC1 (LimeShare Container v1)</b> protocol. When you drop a file:
        </p>
        <ul>
          <li>A cryptographically secure 256-bit master key is generated in browser memory via <code>crypto.getRandomValues</code>.</li>
          <li>HKDF-SHA-256 derives distinct sub-keys: one for file binary streams, and one for the manifest metadata.</li>
          <li>
            The file is segmented into 4 MiB frames. Each frame is encrypted with <b>AES-256-GCM</b> using a deterministic
            STREAM construction: the 12-byte nonce encodes a random 7-byte prefix, a 4-byte big-endian frame counter, and a 1-byte
            last-frame flag.
          </li>
          <li>
            The 32-byte container header carries the exact authenticated plaintext length and serves as Additional Authenticated
            Data (AAD) for every single frame. Truncation, frame swapping, or single-bit corruption causes immediate decryption failure.
          </li>
          <li>
            The master key is appended to the link exclusively inside the <b>URL hash fragment</b> (<code>#k=...</code>). According
            to RFC 3986, browsers never transmit URL fragments to HTTP servers. The server receives only ciphertext.
          </li>
        </ul>
      </section>

      <section id="part-4" style={{ marginBottom: 36 }}>
        <h2>Part 4: The 2026 Platform Attachment Limit Encyclopedia</h2>
        <p>
          Every major communication platform enforces strict ceiling constraints on file attachments. Below is an authoritative
          breakdown of limits across email, enterprise messengers, and chat apps:
        </p>

        <table>
          <thead>
            <tr>
              <th>Platform</th>
              <th>Stated limit</th>
              <th>Practical limit</th>
              <th>Why it fails</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Gmail</b></td>
              <td>25 MB</td>
              <td>≈ 18.5 MB</td>
              <td>Base64 encoding inflation (33% MIME overhead)</td>
            </tr>
            <tr>
              <td><b>Outlook / Exchange</b></td>
              <td>20 MB – 34 MB</td>
              <td>≈ 15 MB – 25 MB</td>
              <td>Corporate mail gateway limits (552 5.3.4 bounce)</td>
            </tr>
            <tr>
              <td><b>Apple Mail Drop</b></td>
              <td>5 GB</td>
              <td>5 GB</td>
              <td>Locks files for 30 days; slow web interface for non-Apple users</td>
            </tr>
            <tr>
              <td><b>WhatsApp</b></td>
              <td>2 GB (docs) / 16 MB (media)</td>
              <td>16 MB</td>
              <td>Recompresses video to 720p; strips 10-bit HDR and 60fps metadata</td>
            </tr>
            <tr>
              <td><b>Telegram</b></td>
              <td>2 GB (free) / 4 GB (Premium)</td>
              <td>2 GB</td>
              <td>Non-E2EE in standard chats; speeds throttled on free tier</td>
            </tr>
            <tr>
              <td><b>Discord</b></td>
              <td>10 MB (free) / 500 MB (Nitro)</td>
              <td>10 MB</td>
              <td>Slashed free limits in late 2023 to force Nitro subscriptions</td>
            </tr>
            <tr>
              <td><b>Slack</b></td>
              <td>1 GB per file</td>
              <td>5 GB workspace total</td>
              <td>Quickly exhausts shared free workspace quotas</td>
            </tr>
            <tr>
              <td><b>Yahoo Mail</b></td>
              <td>25 MB</td>
              <td>≈ 18 MB</td>
              <td>Scanned by ad algorithms; bounces on encoded files</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section id="part-5" style={{ marginBottom: 36 }}>
        <h2>Part 5: Device-to-Device Transfer Matrix</h2>
        <p>
          Sending files between operating systems has long been plagued by proprietary walled gardens. Here is how modern protocols
          compare for cross-device workflows:
        </p>
        <ul>
          <li>
            <b>iPhone to Windows PC:</b> Apple AirDrop is blocked by Apple on non-Mac hardware. Traditional workarounds (iTunes USB syncing,
            iCloud Drive web) are slow and require authentication. <i>LimeDrop P2P connects iPhone Safari to Windows Chrome directly over Wi-Fi
            in 2 seconds via WebRTC DataChannels.</i>
          </li>
          <li>
            <b>Android to Mac:</b> macOS lacks native MTP filesystem support, leading to frequent crashes with Android File Transfer.
            <i>LimeShare transfers full 8K clips or RAW photos over high-speed encrypted pipes without USB cables.</i>
          </li>
          <li>
            <b>Phone to Laptop:</b> When both devices share a local router, LimeDrop keeps traffic strictly local. Data never leaves
            your premises, and transfer speeds reach 50–100+ MB/s uncapped.
          </li>
        </ul>
      </section>

      <section id="part-6" style={{ marginBottom: 36 }}>
        <h2>Part 6: Troubleshooting Bounces, Corrupted Streams &amp; Memory Limits</h2>
        <h3>6.1 Why did my email bounce with error 552?</h3>
        <p>
          SMTP error <code>552 5.3.4 Message size exceeds fixed maximum message size</code> occurs when the combined size of headers,
          email text, and Base64-encoded attachments surpasses the receiving mail server's configured threshold. The solution is to remove
          the attachment and paste an encrypted LimeShare link instead.
        </p>

        <h3>6.2 How does LimeShare prevent corrupted downloads?</h3>
        <p>
          Standard HTTP downloads over unstable mobile connections can silently truncate, leaving damaged ZIP or video files.
          LimeShare's decryptor verifies the 16-byte Poly1305/GCM authentication tag on every 4 MB chunk. If a single byte is missing
          or flipped by network interference, the decryption stream halts instantly, preventing partial corrupted files from reaching disk.
        </p>

        <h3>6.3 How does upload resume work without accounts?</h3>
        <p>
          LimeShare leverages browser <b>IndexedDB</b> to persist the master key (wrapped by an ephemeral device key) and multi-part upload session IDs.
          If your Wi-Fi disconnects or you close the browser tab, reopening the site reloads the pending session. Re-selecting the file
          allows the engine to compute the exact missing byte offset, re-starting the AES-GCM stream from the exact frame where it left off.
        </p>
      </section>

      <section id="part-7" style={{ marginBottom: 36 }}>
        <h2>Part 7: Legal, Regulatory &amp; Compliance Frameworks (GDPR &amp; HIPAA)</h2>
        <p>
          For legal practices, healthcare organizations, and financial auditors, transferring confidential records over third-party
          file sharing creates severe regulatory liability under GDPR (Article 32) and HIPAA Technical Safeguards (45 CFR § 164.312).
        </p>
        <p>
          Because LimeShare operates as a <b>zero-knowledge pipe</b>:
        </p>
        <ul>
          <li><b>No processing of personal data:</b> Files are encrypted before reaching cloud infrastructure. Under GDPR Recital 26, properly encrypted data where the processor has no access to the decryption key does not constitute personal data in the hands of the hosting provider.</li>
          <li><b>Certified Destruction:</b> Upon deletion or burn-on-read, LimeShare signs an Ed25519 cryptographic certificate recording the exact millisecond of erasure and byte count. This certificate can be archived in legal discovery logs as verifiable proof of compliance.</li>
          <li><b>Zero IP retention:</b> Sender IP addresses are transformed into salt-rotated daily hashes and permanently erased within 72 hours, preventing network tracking.</li>
        </ul>
      </section>

      <div style={{ padding: 26, background: "rgba(0, 255, 102, 0.05)", border: "1px solid rgba(0, 255, 102, 0.25)", borderRadius: "var(--radius)", textAlign: "center", marginTop: 40 }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 20 }}>Experience Zero-Knowledge File Transfer</h3>
        <p className="hint" style={{ margin: "0 0 20px", maxWidth: 520, marginInline: "auto" }}>
          Transfer files up to 3 GB free with client-side AES-256 encryption. No registration, no ads, auto-purged links.
        </p>
        <Link to="/" className="btn block" style={{ maxWidth: 320, margin: "0 auto", textDecoration: "none" }}>
          <span>Start Encrypted Transfer</span> <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}
